import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpCacheInterceptor } from '../interceptors/http-cache.interceptor';

/**
 * Cache Invalidation Service - Phase 3A Implementation
 *
 * Manages cache invalidation based on user actions and application events.
 * Ensures cached data doesn't become stale when underlying data changes.
 *
 * Key responsibilities:
 * - Automatic cache clearing on logout
 * - Business-specific cache invalidation on business switching
 * - User data cache clearing on profile updates
 * - Route-based cache invalidation
 * - Manual cache management methods
 */
@Injectable({
  providedIn: 'root'
})
export class CacheInvalidationService {

  private invalidationRules: Array<{
    trigger: string;
    pattern: string;
    description: string;
  }> = [
    {
      trigger: 'logout',
      pattern: '', // Clear all cache
      description: 'Clear all cached data on user logout'
    },
    {
      trigger: 'business.switch',
      pattern: 'business/',
      description: 'Clear business-related cache when switching business'
    },
    {
      trigger: 'user.update',
      pattern: 'user/',
      description: 'Clear user cache when profile is updated'
    },
    {
      trigger: 'settings.update',
      pattern: 'settings',
      description: 'Clear settings cache when configuration changes'
    }
  ];

  constructor(
    private router: Router
    // Note: HttpCacheInterceptor will be injected when needed to avoid circular dependencies
  ) {
    this.setupRouteBasedInvalidation();
  }

  /**
   * Setup automatic cache invalidation based on route changes
   */
  private setupRouteBasedInvalidation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.url);
    });
  }

  /**
   * Handle cache invalidation on route changes
   */
  private handleRouteChange(url: string): void {
    // Invalidate business cache when leaving business pages
    if (url.includes('/business/') && !url.includes('/business/')) {
      // User navigated away from business section
      // Could implement selective invalidation here
    }

    // Invalidate user cache when leaving profile pages
    if (url.includes('/profile/') && !url.includes('/profile/')) {
      // User navigated away from profile section
      // Could implement selective invalidation here
    }

    // Add more route-based invalidation rules as needed
  }

  /**
   * Clear cache on user logout
   * Call this method from your authentication service
   */
  onLogout(): void {
    try {
      // Get interceptor instance (avoiding circular dependency)
      const cacheInterceptor = this.getCacheInterceptor();
      if (cacheInterceptor) {
        cacheInterceptor.clearCache();
        console.log('🧹 All cache cleared on logout');
      }

      // Additional cleanup
      this.clearLocalStorageCache();
      this.logInvalidationEvent('logout', 'all');

    } catch (error) {
      console.error('Error clearing cache on logout:', error);
    }
  }

  /**
   * Clear business-related cache when switching businesses
   * Call this method when user switches business context
   */
  onBusinessSwitch(oldBusinessId?: string, newBusinessId?: string): void {
    try {
      const cacheInterceptor = this.getCacheInterceptor();
      if (cacheInterceptor) {
        // Clear business-specific cache
        cacheInterceptor.clearCache('business/');
        cacheInterceptor.clearCache('organization/');

        console.log(`🧹 Business cache cleared (${oldBusinessId} → ${newBusinessId})`);
      }

      this.logInvalidationEvent('business.switch', 'business/', {
        oldBusinessId,
        newBusinessId
      });

    } catch (error) {
      console.error('Error clearing business cache:', error);
    }
  }

  /**
   * Clear user-related cache when profile is updated
   * Call this method after user profile updates
   */
  onUserUpdate(userId?: string): void {
    try {
      const cacheInterceptor = this.getCacheInterceptor();
      if (cacheInterceptor) {
        cacheInterceptor.clearCache('user/');
        console.log(`🧹 User cache cleared for user: ${userId}`);
      }

      this.logInvalidationEvent('user.update', 'user/', { userId });

    } catch (error) {
      console.error('Error clearing user cache:', error);
    }
  }

  /**
   * Clear settings cache when configuration changes
   * Call this method after settings updates
   */
  onSettingsUpdate(settingsType?: string): void {
    try {
      const cacheInterceptor = this.getCacheInterceptor();
      if (cacheInterceptor) {
        cacheInterceptor.clearCache('settings');
        cacheInterceptor.clearCache('configuration');
        console.log(`🧹 Settings cache cleared: ${settingsType}`);
      }

      this.logInvalidationEvent('settings.update', 'settings', { settingsType });

    } catch (error) {
      console.error('Error clearing settings cache:', error);
    }
  }

  /**
   * Clear subscription/plan cache when subscription changes
   * Call this method after subscription updates
   */
  onSubscriptionUpdate(planType?: string): void {
    try {
      const cacheInterceptor = this.getCacheInterceptor();
      if (cacheInterceptor) {
        cacheInterceptor.clearCache('subscription');
        cacheInterceptor.clearCache('plan');
        console.log(`🧹 Subscription cache cleared: ${planType}`);
      }

      this.logInvalidationEvent('subscription.update', 'subscription', { planType });

    } catch (error) {
      console.error('Error clearing subscription cache:', error);
    }
  }

  /**
   * Manual cache invalidation methods
   */

  /**
   * Clear all cached data manually
   */
  invalidateAllCache(): void {
    const cacheInterceptor = this.getCacheInterceptor();
    if (cacheInterceptor) {
      cacheInterceptor.clearCache();
      console.log('🧹 Manual: All cache cleared');
    }
  }

  /**
   * Clear cache for specific URL pattern
   * @param pattern - URL pattern to clear
   */
  invalidateCacheByPattern(pattern: string): void {
    const cacheInterceptor = this.getCacheInterceptor();
    if (cacheInterceptor) {
      cacheInterceptor.clearCache(pattern);
      console.log(`🧹 Manual: Cache cleared for pattern: ${pattern}`);
    }
  }

  /**
   * Clear cache for current user's data
   */
  invalidateCurrentUserCache(): void {
    const userId = localStorage.getItem('userId');
    if (userId) {
      this.onUserUpdate(userId);
    }
  }

  /**
   * Clear cache for current business context
   */
  invalidateCurrentBusinessCache(): void {
    const businessId = localStorage.getItem('businessId');
    this.onBusinessSwitch(businessId, businessId);
  }

  /**
   * Get cache invalidation statistics
   */
  getInvalidationStats(): any {
    const stats = JSON.parse(localStorage.getItem('cache-invalidation-stats') || '{}');
    return {
      totalInvalidations: stats.total || 0,
      byTrigger: stats.byTrigger || {},
      lastInvalidation: stats.lastInvalidation || null,
      rules: this.invalidationRules
    };
  }

  /**
   * Private helper methods
   */

  /**
   * Get cache interceptor instance (avoiding circular dependency)
   */
  private getCacheInterceptor(): HttpCacheInterceptor | null {
    try {
      // This would be injected properly in a real implementation
      // For now, we'll use a workaround to avoid circular dependencies
      return (window as any)._cacheInterceptor || null;
    } catch {
      return null;
    }
  }

  /**
   * Clear localStorage-based cache entries
   */
  private clearLocalStorageCache(): void {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('cache-') || key.startsWith('api-cache-'))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`🧹 Cleared ${keysToRemove.length} localStorage cache entries`);
    }
  }

  /**
   * Log invalidation events for monitoring
   */
  private logInvalidationEvent(trigger: string, pattern: string, metadata?: any): void {
    const stats = JSON.parse(localStorage.getItem('cache-invalidation-stats') || '{}');

    stats.total = (stats.total || 0) + 1;
    stats.byTrigger = stats.byTrigger || {};
    stats.byTrigger[trigger] = (stats.byTrigger[trigger] || 0) + 1;
    stats.lastInvalidation = {
      timestamp: Date.now(),
      trigger,
      pattern,
      metadata
    };

    localStorage.setItem('cache-invalidation-stats', JSON.stringify(stats));

    // Also send to analytics if needed
    this.sendInvalidationAnalytics(trigger, pattern, metadata);
  }

  /**
   * Send invalidation analytics (optional)
   */
  private sendInvalidationAnalytics(trigger: string, pattern: string, metadata?: any): void {
    // Implementation would depend on your analytics service
    // Example:
    // this.analytics.track('cache_invalidation', {
    //   trigger,
    //   pattern,
    //   ...metadata
    // });
  }

  /**
   * Setup integration with application services
   * This method should be called from app initialization
   */
  setupServiceIntegrations(): void {
    // Example integrations - adapt to your services

    // Integration with auth service
    // this.authService.logout$.subscribe(() => this.onLogout());

    // Integration with business service
    // this.businessService.businessChanged$.subscribe((event) => {
    //   this.onBusinessSwitch(event.oldId, event.newId);
    // });

    // Integration with user service
    // this.userService.profileUpdated$.subscribe((userId) => {
    //   this.onUserUpdate(userId);
    // });

    console.log('🔗 Cache invalidation service integrations setup');
  }

  /**
   * Add custom invalidation rule
   */
  addInvalidationRule(trigger: string, pattern: string, description: string): void {
    this.invalidationRules.push({ trigger, pattern, description });
    console.log(`✅ Added invalidation rule: ${trigger} → ${pattern}`);
  }

  /**
   * Remove invalidation rule
   */
  removeInvalidationRule(trigger: string): boolean {
    const initialLength = this.invalidationRules.length;
    this.invalidationRules = this.invalidationRules.filter(rule => rule.trigger !== trigger);

    if (this.invalidationRules.length < initialLength) {
      console.log(`🗑️ Removed invalidation rule: ${trigger}`);
      return true;
    }
    return false;
  }
}